import React from 'react';

import { SKELETON } from '../../theme/tokens';
import { Stack, Layer, OverlayRoot, OverlayLayer } from './Reveal.styles';

export type RevealProps = {
  /** While true the placeholder shows; when it flips false the real `children`
   *  cross-fade in over the fading placeholder. Pair it with `useMockEndpoint`'s
   *  `loading` (or a real query's). */
  loading: boolean;
  /** What stands in while loading — typically one or more `<Skeleton/>` blocks,
   *  sized to the box the real value occupies. */
  placeholder: React.ReactNode;
  /** The real value, revealed once `loading` ends. */
  children: React.ReactNode;
  /**
   * Overlay variant for content that lives in a box its parent already sizes (a
   * progress track, a gauge ring) and that plays its OWN entrance animation on
   * reveal (a bar growing, a ring drawing in). The placeholder covers the box as
   * an absolute layer and fades out while the real content — mounted only once
   * `loading` ends, so its animation fires on reveal — animates in underneath.
   *
   * The default (stacked) variant instead keeps both layers in one grid cell and
   * cross-fades their opacity; use it for in-flow values that size themselves.
   */
  overlay?: boolean;
  /** Style hook for spacing/positioning from the consuming `.styles.ts`. */
  className?: string;
};

/**
 * Cross-fades a loading placeholder to the real value: while `loading`, the
 * placeholder shows (and eases in via `Skeleton`'s own mount fade); when it
 * resolves, the value fades in as the placeholder fades out, so a figure / gauge
 * arrives as a soft swap instead of a pop. A thin wrapper around the existing
 * `{loading ? <Skeleton/> : value}` idiom — move the skeleton into `placeholder`
 * and the value into `children`. See {@link RevealProps.overlay} for the two
 * layout variants.
 */
export const Reveal = ({ loading, placeholder, children, overlay = false, className }: RevealProps): React.ReactElement => {
  // Keep the placeholder mounted through its fade-out, then drop it so its sheen
  // animation stops and the DOM settles to just the real content.
  const [showPlaceholder, setShowPlaceholder] = React.useState(loading);

  React.useEffect(() => {
    if (loading) {
      setShowPlaceholder(true);
      return undefined;
    }
    const timer = setTimeout(() => setShowPlaceholder(false), SKELETON.CROSSFADE_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  if (overlay) {
    return (
      <OverlayRoot className={className}>
        {/* Mounted only on reveal so the content's own entrance animation plays
            as the placeholder fades out (not unseen behind it). */}
        {loading ? null : children}
        {showPlaceholder ? (
          <OverlayLayer $visible={loading} aria-hidden="true">
            {placeholder}
          </OverlayLayer>
        ) : null}
      </OverlayRoot>
    );
  }

  return (
    <Stack className={className}>
      <Layer $visible={!loading} aria-hidden={loading || undefined}>
        {children}
      </Layer>
      {showPlaceholder ? (
        <Layer $visible={loading} aria-hidden="true">
          {placeholder}
        </Layer>
      ) : null}
    </Stack>
  );
};

export default Reveal;
